import LoginForm from '@/components/auth/LoginForm';
import LoadingSpinner from '@/components/ui/loader/LoadingSpinner';
import { useLoginData } from '@/hooks/auth/useLoginData';
import { useAuthStore } from '@/stores/auth.store';
import { Store, ShoppingBag, TrendingUp } from 'lucide-react';
import React from 'react';
import { Navigate } from 'react-router-dom';

const Login: React.FC = () => {
  const { errors, handleSubmit, isLoggingIn, onSubmit, register, isValid, isSubmitting } =
    useLoginData();

  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary-500 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-20 left-20 w-64 h-64 bg-accent-400 rounded-full opacity-20"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-accent-400 rounded-full opacity-10"></div>
        <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-white rounded-full opacity-10"></div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12 text-white">
          <img
            src="/images/Umenda.svg"
            alt="Umenda Logo"
            className="h-20 w-auto mb-8 brightness-0 invert"
          />
          <h1 className="text-4xl font-bold mb-4 text-center">Vendor Portal</h1>
          <p className="text-lg text-center text-white/80 max-w-md">
            Manage your restaurant, track orders, and grow your business with our powerful vendor tools.
          </p>

          {/* Features */}
          <div className="mt-12 grid grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-accent-400/20 rounded-full flex items-center justify-center mb-3">
                <Store className="w-6 h-6 text-accent-400" />
              </div>
              <div className="text-sm text-white/70">Manage Menu</div>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-accent-400/20 rounded-full flex items-center justify-center mb-3">
                <ShoppingBag className="w-6 h-6 text-accent-400" />
              </div>
              <div className="text-sm text-white/70">Track Orders</div>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-accent-400/20 rounded-full flex items-center justify-center mb-3">
                <TrendingUp className="w-6 h-6 text-accent-400" />
              </div>
              <div className="text-sm text-white/70">Grow Sales</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <img
                src="/images/Umenda.svg"
                alt="Umenda Logo"
                className="h-12 w-auto"
              />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Vendor Portal</h2>
          </div>

          {/* Form Header */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign in to your account</h2>
            <p className="text-gray-600">
              Enter your credentials to manage your restaurant
            </p>
          </div>

          <LoginForm
            errors={errors}
            handleSubmit={handleSubmit}
            isLoggingIn={isLoggingIn}
            onSubmit={onSubmit}
            register={register}
            isValid={isValid}
            isSubmitting={isSubmitting}
          />

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Protected by enterprise-grade security
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
